/**
 * Answer the agent's document-tool interrupts with Office.js.
 *
 * The backend pauses the run with `{ type: "word_tool", tool, call_id, ...args }`.
 * This hook executes the request and resumes with
 * `{ type: "word_tool", call_id, result }`, or `{ ..., error }` when the
 * request cannot be carried out, so the run never hangs on a pane failure.
 */
import type { TemplateStyleMap, TemplateStyleRef } from "./identity";
import type { AnswerPlacement, PullStyleChoice } from "./questionBlocks";
import type { PullDirectives } from "../tender/useQuestions";
import { useAuiState } from "@assistant-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useInterrupt } from "@/agents-v2/interrupts/use-interrupt";
import { trackInsert } from "../analytics";
import { insertTally, isDocumentInsert } from "../analyticsEvents";
import { asArray, asRecord, getStringValue, parseMaybeJson } from "@/agents-v2/utils/unknown";

import { formatDocument, type FormatOp, type FormatResult } from "./formatting";
import { type ReviewAction, reviewChanges, scopeFor } from "./review";
import { applyTemplate, type ApplyTemplateRequest } from "./applyTemplate";
import { type ProbePayload, probeInserts } from "./insertProbe";
import { undoLastChange } from "./journal";
import { applyPageSetup, type PageSetupRequest } from "./pageSetup";
import { importTemplateStyles } from "./templateStyles";
import {
  addDocumentComments,
  applyTemplateHeadersFooters,
  authoredRevisionsActive,
  type HeadersFootersApply,
  insertTemplatePages,
  type TemplatePagesInsert,
  applyDocumentEdits,
  type CommentResult,
  type DocumentComment,
  type EditOp,
  type EditResult,
  type ImageInsert,
  insertDocumentImage,
  openInMytender,
  readDocumentRange,
  searchDocument,
} from "./wordTools";

export const WORD_TOOL_INTERRUPT_TYPE = "word_tool";

export type WordToolRequest =
  | { tool: "read_document_range"; callId: string; start: number; end: number }
  | { tool: "search_document"; callId: string; query: string }
  | { tool: "edit_document"; callId: string; ops: EditOp[] }
  | { tool: "add_document_comment"; callId: string; comments: DocumentComment[] }
  | { tool: "insert_document_image"; callId: string; insert: ImageInsert }
  | { tool: "open_in_mytender"; callId: string; view: string | null; reason: string | null }
  | { tool: "format_document"; callId: string; ops: FormatOp[] }
  | { tool: "review_document_changes"; callId: string; action: ReviewAction }
  | { tool: "apply_template_styles"; callId: string; docxBase64: string }
  | { tool: "apply_template"; callId: string; request: ApplyTemplateRequest }
  | { tool: "pull_from_mytender"; callId: string; sectionIds: string[]; directives: PullDirectives }
  | { tool: "write_question_answer"; callId: string; sectionId: string; markdown: string }
  | { tool: "show_question"; callId: string; sectionId: string }
  | { tool: "push_to_mytender"; callId: string; sectionIds: string[] }
  | { tool: "set_page_setup"; callId: string; request: PageSetupRequest }
  | { tool: "insert_template_pages"; callId: string; insert: TemplatePagesInsert }
  | { tool: "apply_template_headers_footers"; callId: string; apply: HeadersFootersApply }
  | { tool: "undo_last_change"; callId: string; steps: number }
  | { tool: "probe_document_inserts"; callId: string; probe: ProbePayload };

const toInt = (value: unknown): number | null => (typeof value === "number" && Number.isInteger(value) ? value : null);

/** The server's reading of which styles the template body uses, or nothing when it is not shaped right. */
const styleMap = (value: unknown): TemplateStyleMap | undefined => {
  const record = asRecord(value);
  const ref = (v: unknown): TemplateStyleRef | null => {
    const r = asRecord(v);
    const id = getStringValue(r, "id");
    return id ? { id, name: getStringValue(r, "name") ?? id } : null;
  };
  const listRef = (v: unknown) => {
    const base = ref(v);
    return base ? { ...base, has_numbering: asRecord(v).has_numbering === true } : null;
  };
  const body = ref(record.body);
  if (!body) return undefined;
  const headings: Record<string, TemplateStyleRef> = {};
  for (const [level, v] of Object.entries(asRecord(record.headings))) {
    const h = ref(v);
    if (h) headings[level] = h;
  }
  return {
    body,
    bullet: listRef(record.bullet),
    number: listRef(record.number),
    headings,
    table: ref(record.table),
    response_start: getStringValue(record, "response_start") ?? null,
  };
};

const PLACEMENTS = new Set(["auto", "under_heading", "after_paragraph", "start", "end", "before_closing_pages"]);

/** The agent's placement and style decisions on a pull, as far as they are well-formed. */
const pullDirectives = (record: Record<string, unknown>): PullDirectives => {
  const placements = asArray(record.placements)
    .map((item) => {
      const r = asRecord(item);
      const sectionId = getStringValue(r, "section_id");
      const position = getStringValue(r, "position") ?? "auto";
      if (!sectionId || !PLACEMENTS.has(position)) return null;
      const index = toInt(r.index);
      return {
        section_id: sectionId,
        position: position as AnswerPlacement["position"],
        ...(index !== null && { index }),
        ...(typeof r.expected_text === "string" && { expected_text: r.expected_text }),
        ...(typeof r.include_heading === "boolean" && { include_heading: r.include_heading }),
      };
    })
    .filter((p): p is NonNullable<typeof p> => p !== null);
  const s = asRecord(record.styles);
  const pick = (key: string) => getStringValue(s, key) ?? undefined;
  const styles: PullStyleChoice = {
    body: pick("body"),
    bullet: pick("bullet"),
    number: pick("number"),
    heading_1: pick("heading_1"),
    heading_2: pick("heading_2"),
    heading_3: pick("heading_3"),
    table: pick("table"),
  };
  return { placements, styles: Object.values(styles).some(Boolean) ? styles : null };
};

/** The insert probe's lighter forms of a cover: `[{name, docx_base64}]`, or nothing. */
const fallbacks = (value: unknown): { name: string; docx_base64: string }[] | undefined => {
  const items = asArray(value)
    .map((item) => {
      const record = asRecord(item);
      const name = getStringValue(record, "name");
      const docx = getStringValue(record, "docx_base64");
      return name && docx ? { name, docx_base64: docx } : null;
    })
    .filter((item): item is { name: string; docx_base64: string } => item !== null);
  return items.length ? items : undefined;
};

const parseOp = (value: unknown): EditOp | null => {
  const record = asRecord(value);
  const op = getStringValue(record, "op");
  const index = toInt(record.index);
  const expected = typeof record.expected_text === "string" ? record.expected_text : null;
  if (!op || index === null || expected === null) return null;
  if (
    op !== "replace" &&
    op !== "insert_after" &&
    op !== "insert_before" &&
    op !== "delete" &&
    op !== "delete_table" &&
    op !== "replace_table"
  )
    return null;
  const newText = typeof record.new_text === "string" ? record.new_text : undefined;
  return { op, index, expected_text: expected, ...(newText !== undefined && { new_text: newText }) };
};

const parseComment = (value: unknown): DocumentComment | null => {
  const record = asRecord(value);
  const index = toInt(record.index);
  const expected = typeof record.expected_text === "string" ? record.expected_text : null;
  const comment = getStringValue(record, "comment");
  if (index === null || expected === null || !comment) return null;
  const quote = getStringValue(record, "quote");
  return { index, expected_text: expected, comment, ...(quote && { quote }) };
};

const num = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;
const str = (value: unknown): string | undefined => (typeof value === "string" && value.trim() ? value : undefined);
const bool = (value: unknown): boolean | undefined => (typeof value === "boolean" ? value : undefined);

const parseFont = (value: unknown): FormatOp["font"] | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const r = asRecord(value);
  const font = {
    name: str(r.name),
    size: num(r.size),
    color: str(r.color),
    highlight: r.highlight === null ? null : str(r.highlight),
    bold: bool(r.bold),
    italic: bool(r.italic),
    underline: bool(r.underline),
  };
  return Object.values(font).some((v) => v !== undefined) ? font : undefined;
};

const parseParagraphSpec = (value: unknown): NonNullable<FormatOp["paragraph"]> | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const r = asRecord(value);
  const alignment = str(r.alignment);
  const spec: NonNullable<FormatOp["paragraph"]> = {
    alignment:
      alignment === "left" || alignment === "centered" || alignment === "right" || alignment === "justified"
        ? alignment
        : undefined,
    line_spacing: num(r.line_spacing),
    space_before: num(r.space_before),
    space_after: num(r.space_after),
    style: str(r.style),
  };
  return Object.values(spec).some((v) => v !== undefined) ? spec : undefined;
};

const parseFormatOp = (value: unknown): FormatOp | null => {
  const r = asRecord(value);
  const font = parseFont(r.font);
  const paragraph = parseParagraphSpec(r.paragraph);
  if (!font && !paragraph) return null;
  switch (r.target) {
    case "document":
      return { target: "document", font, paragraph };
    case "style": {
      const name = str(r.style_name);
      return name ? { target: "style", style_name: name, font, paragraph } : null;
    }
    case "paragraphs": {
      const start = toInt(r.start);
      const end = toInt(r.end);
      const expected = typeof r.expected_text === "string" ? r.expected_text : null;
      if (start === null || end === null || expected === null) return null;
      return { target: "paragraphs", start, end: Math.max(start, end), expected_text: expected, font, paragraph };
    }
    default:
      return null;
  }
};

/**
 * Parse a raw interrupt value into a Word tool request.
 * @param value - The interrupt value (object or JSON string).
 * @returns The request, or null when it is some other interrupt.
 */
export const parseWordToolInterrupt = (value: unknown): WordToolRequest | null => {
  const record = asRecord(parseMaybeJson(value));
  if (record.type !== WORD_TOOL_INTERRUPT_TYPE) return null;
  const callId = getStringValue(record, "call_id");
  const tool = getStringValue(record, "tool");
  if (!callId || !tool) return null;
  switch (tool) {
    case "read_document_range": {
      const start = toInt(record.start);
      const end = toInt(record.end);
      if (start === null || end === null) return null;
      return { tool, callId, start, end };
    }
    case "search_document": {
      const query = getStringValue(record, "query");
      if (!query) return null;
      return { tool, callId, query };
    }
    case "edit_document": {
      // All or nothing: dropping a malformed op and applying the rest would
      // half-apply the agent's change without telling it.
      const raw = asArray(record.ops);
      const ops = raw.map(parseOp).filter((op): op is EditOp => op !== null);
      if (!ops.length || ops.length !== raw.length) return null;
      return { tool, callId, ops };
    }
    case "add_document_comment": {
      const comments = asArray(record.comments)
        .map(parseComment)
        .filter((c): c is DocumentComment => c !== null);
      if (!comments.length) return null;
      return { tool, callId, comments };
    }
    case "insert_document_image": {
      const index = toInt(record.index);
      const expected = typeof record.expected_text === "string" ? record.expected_text : null;
      const image = getStringValue(record, "image");
      if (index === null || expected === null || !image) return null;
      return {
        tool,
        callId,
        insert: {
          index,
          expected_text: expected,
          position: record.position === "before" ? "before" : "after",
          alt: getStringValue(record, "alt"),
          image,
        },
      };
    }
    case "open_in_mytender":
      return { tool, callId, view: getStringValue(record, "view"), reason: getStringValue(record, "reason") };
    case "format_document": {
      const ops = asArray(record.ops)
        .map(parseFormatOp)
        .filter((op): op is FormatOp => op !== null);
      if (!ops.length) return null;
      return { tool, callId, ops };
    }
    case "review_document_changes": {
      const action = getStringValue(record, "action");
      if (action !== "count" && action !== "accept_all" && action !== "reject_all") return null;
      return { tool, callId, action };
    }
    case "apply_template_styles": {
      const docx = getStringValue(record, "docx_base64");
      if (!docx) return null;
      return { tool, callId, docxBase64: docx };
    }
    case "pull_from_mytender": {
      const ids = asArray(record.section_ids).filter((x): x is string => typeof x === "string");
      return { tool, callId, sectionIds: ids, directives: pullDirectives(record) };
    }
    case "push_to_mytender": {
      const ids = asArray(record.section_ids).filter((x): x is string => typeof x === "string");
      return { tool, callId, sectionIds: ids };
    }
    case "show_question": {
      const sectionId = getStringValue(record, "section_id");
      if (!sectionId) return null;
      return { tool, callId, sectionId };
    }
    case "write_question_answer": {
      const sectionId = getStringValue(record, "section_id");
      const markdown = getStringValue(record, "markdown");
      if (!sectionId || !markdown) return null;
      return { tool, callId, sectionId, markdown };
    }
    case "set_page_setup": {
      const margins =
        record.margins && typeof record.margins === "object"
          ? (record.margins as PageSetupRequest["margins"])
          : undefined;
      const orientation =
        record.orientation === "landscape" || record.orientation === "portrait" ? record.orientation : undefined;
      const paper = getStringValue(record, "paper_size") as PageSetupRequest["paper_size"] | null;
      return {
        tool,
        callId,
        request: {
          ...(margins && { margins }),
          ...(orientation && { orientation }),
          ...(paper && { paper_size: paper }),
          ...(toInt(record.start_index) !== null && { start_index: toInt(record.start_index) as number }),
          ...(toInt(record.end_index) !== null && { end_index: toInt(record.end_index) as number }),
          ...(typeof record.expected_text === "string" && { expected_text: record.expected_text }),
        },
      };
    }
    case "apply_template": {
      const docx = getStringValue(record, "docx_base64");
      const body = getStringValue(record, "body");
      if (!docx || (body !== "auto" && body !== "replace" && body !== "frame" && body !== "none")) return null;
      const frame = asRecord(record.frame);
      const nums = (v: unknown) =>
        asArray(v)
          .map(toInt)
          .filter((n): n is number => n !== null);
      const opt = (key: string) =>
        typeof record[key] === "string" ? (record[key] as string) : record[key] === null ? null : undefined;
      return {
        tool,
        callId,
        request: {
          template_file_id: getStringValue(record, "template_file_id") ?? undefined,
          template_name: getStringValue(record, "template_name") ?? undefined,
          styles: record.styles !== false,
          body,
          docx_base64: docx,
          confirm_replace: record.confirm_replace === true,
          front_docx_base64: getStringValue(record, "front_docx_base64") ?? undefined,
          front_ooxml: getStringValue(record, "front_ooxml") ?? undefined,
          middle_docx_base64: getStringValue(record, "middle_docx_base64") ?? undefined,
          middle_ooxml: getStringValue(record, "middle_ooxml") ?? undefined,
          back_docx_base64: getStringValue(record, "back_docx_base64") ?? undefined,
          back_ooxml: getStringValue(record, "back_ooxml") ?? undefined,
          docx_ooxml: getStringValue(record, "docx_ooxml") ?? undefined,
          frame: { front: nums(frame.front), back: nums(frame.back) },
          page_count: toInt(record.page_count) ?? 0,
          header_ooxml: opt("header_ooxml"),
          footer_ooxml: opt("footer_ooxml"),
          first_header_ooxml: opt("first_header_ooxml"),
          first_footer_ooxml: opt("first_footer_ooxml"),
          even_header_ooxml: opt("even_header_ooxml"),
          even_footer_ooxml: opt("even_footer_ooxml"),
          theme_numbering_ooxml: opt("theme_numbering_ooxml"),
          page_setup:
            record.page_setup && typeof record.page_setup === "object"
              ? (record.page_setup as ApplyTemplateRequest["page_setup"])
              : null,
          style_map: styleMap(record.style_map),
        },
      };
    }
    case "insert_template_pages": {
      const docx = getStringValue(record, "docx_base64");
      const position = getStringValue(record, "position");
      if (!docx || (position !== "start" && position !== "end" && position !== "after_paragraph")) return null;
      const index = toInt(record.index);
      return {
        tool,
        callId,
        insert: {
          docx_base64: docx,
          ...(getStringValue(record, "ooxml") && { ooxml: getStringValue(record, "ooxml") as string }),
          position,
          ...(index !== null && { index }),
          ...(typeof record.expected_text === "string" && { expected_text: record.expected_text }),
        },
      };
    }
    case "apply_template_headers_footers": {
      const header = getStringValue(record, "header_ooxml");
      const footer = getStringValue(record, "footer_ooxml");
      if (!header && !footer) return null;
      return { tool, callId, apply: { header_ooxml: header ?? null, footer_ooxml: footer ?? null } };
    }
    case "undo_last_change":
      return { tool, callId, steps: Math.max(1, toInt(record.steps) ?? 1) };
    case "probe_document_inserts": {
      const original = getStringValue(record, "original_docx_base64");
      const front = getStringValue(record, "front_docx_base64");
      const plain = getStringValue(record, "front_docx_plain_base64");
      const minimal = getStringValue(record, "minimal_docx_base64");
      const paragraph = getStringValue(record, "ooxml_paragraph");
      if (!original || !front || !plain || !minimal || !paragraph) return null;
      return {
        tool,
        callId,
        probe: {
          original_docx_base64: original,
          front_docx_base64: front,
          front_docx_plain_base64: plain,
          minimal_docx_base64: minimal,
          ooxml_paragraph: paragraph,
          front_fallbacks: fallbacks(record.front_fallbacks),
          front_ooxml: getStringValue(record, "front_ooxml"),
          theme_numbering_ooxml: getStringValue(record, "theme_numbering_ooxml"),
        },
      };
    }
    default:
      return null;
  }
};

/** What the pane shows the user after a batch of edits. */
export type EditNotice = {
  kind: "edits" | "comments" | "formatting";
  applied: number;
  stale: number;
  failed: number;
  /** Edits: tracked changes were available. Comments: comments were available. */
  trackedChanges: boolean;
  /** Edits landed as revisions authored by mytender.io. */
  authored?: boolean;
  /** Why they didn't, when the host refused. */
  authoredError?: string | null;
  at: number;
};

const commentNoticeFor = (result: CommentResult): EditNotice => ({
  kind: "comments",
  applied: result.outcomes.filter((o) => o.status === "added").length,
  stale: result.outcomes.filter((o) => o.status === "stale" || o.status === "missing").length,
  failed: result.outcomes.filter((o) => o.status === "error").length,
  trackedChanges: result.comments_supported,
  at: Date.now(),
});

const noticeFor = (result: EditResult): EditNotice => ({
  kind: "edits",
  applied: result.outcomes.filter((o) => o.status === "applied").length,
  stale: result.outcomes.filter((o) => o.status === "stale" || o.status === "missing").length,
  failed: result.outcomes.filter((o) => o.status === "error").length,
  trackedChanges: result.tracked_changes,
  authored: result.authored,
  authoredError: result.authored_error ?? null,
  at: Date.now(),
});

const formatNoticeFor = (result: FormatResult): EditNotice => ({
  kind: "formatting",
  applied: result.outcomes.filter((o) => o.status === "applied").length,
  stale: result.outcomes.filter((o) => o.status === "stale" || o.status === "missing").length,
  failed: result.outcomes.filter((o) => o.status === "error" || o.status === "unsupported").length,
  trackedChanges: result.tracked_changes,
  at: Date.now(),
});

export type WordToolEnvironment = {
  /** The bid this document is linked to, for deep links. */
  bidId: string | null;
  /** Pull questions from mytender into the document (all when empty). */
  pull?: (sectionIds: string[], directives: PullDirectives) => Promise<unknown>;
  /** Ask the user to confirm a push in the chat, then push; resolves with what happened. */
  confirmPush?: (sectionIds: string[]) => Promise<unknown>;
  /** Write a drafted answer into a question's place; refuses one that already has text. */
  writeAnswer?: (sectionId: string, markdown: string) => Promise<unknown>;
  /** Scroll the document to a question's answer and outline it. */
  showQuestion?: (sectionId: string) => Promise<unknown>;
};

/**
 * Execute a Word tool request.
 * @param request - The parsed request.
 * @param env - What the pane knows that the request does not carry.
 * @returns The tool result the run resumes with.
 */
export const executeWordTool = async (request: WordToolRequest, env: WordToolEnvironment): Promise<unknown> => {
  switch (request.tool) {
    case "read_document_range":
      return readDocumentRange(request.start, request.end);
    case "search_document":
      return searchDocument(request.query);
    case "edit_document":
      return applyDocumentEdits(request.ops);
    case "add_document_comment":
      return addDocumentComments(request.comments);
    case "insert_document_image":
      return insertDocumentImage(request.insert);
    case "open_in_mytender":
      return openInMytender(env.bidId, request.view);
    case "format_document":
      return formatDocument(request.ops);
    case "review_document_changes":
      return reviewChanges(request.action, scopeFor(authoredRevisionsActive()));
    case "apply_template_styles":
      return importTemplateStyles(request.docxBase64);
    case "apply_template":
      return applyTemplate(request.request);
    case "set_page_setup":
      return applyPageSetup(request.request);
    case "pull_from_mytender":
      if (!env.pull) return { status: "unavailable", message: "Link a tender to get its answers." };
      return { outcomes: await env.pull(request.sectionIds, request.directives) };
    case "show_question":
      if (!env.showQuestion) return { status: "unavailable", message: "Link a tender to show its questions." };
      return env.showQuestion(request.sectionId);
    case "write_question_answer":
      if (!env.writeAnswer) return { status: "unavailable", message: "Link a tender to write answers." };
      return env.writeAnswer(request.sectionId, request.markdown);
    case "push_to_mytender":
      if (!env.confirmPush) return { status: "unavailable", message: "Link a tender to send answers." };
      return env.confirmPush(request.sectionIds);
    case "insert_template_pages":
      return insertTemplatePages(request.insert);
    case "apply_template_headers_footers":
      return applyTemplateHeadersFooters(request.apply);
    case "undo_last_change":
      return undoLastChange(request.steps);
    case "probe_document_inserts":
      return probeInserts(request.probe);
  }
};

/**
 * Mount inside the runtime provider. Resolves every `word_tool` interrupt
 * and exposes the last edit outcome for the pane to show.
 * @param onAfterEdit - Called after edits land, so the snapshot can refresh.
 * @returns The last edit notice, and a way to dismiss it.
 */
export const useWordToolInterrupts = (env: WordToolEnvironment, onAfterEdit?: () => void) => {
  const [notice, setNotice] = useState<EditNotice | null>(null);
  const envRef = useRef(env);
  envRef.current = env;
  const onAfterEditRef = useRef(onAfterEdit);
  onAfterEditRef.current = onAfterEdit;

  const pending = useInterrupt(parseWordToolInterrupt);
  // The interrupt is announced while the interrupted run's stream is still
  // open. Resuming at that moment races the runtime closing that run's
  // tool-call streams (the resumed run's tool result then lands on a closed
  // stream and throws), so the resume waits until the run has settled.
  const isRunning = useAuiState((s) => s.thread.isRunning);
  // Re-entry guard for one pending interrupt: the effect re-fires whenever
  // `isRunning` flips while a tool is still executing, and the same request
  // must not run twice. Keyed on the pending object, not the call id: a call
  // id is a pure function of the request, so a later identical request (the
  // model re-reading after a stale edit) is a new pending object and runs,
  // where a call-id key left it spinning forever (CodeRabbit on #1295).
  const handledRef = useRef<typeof pending>(null);

  useEffect(() => {
    if (!pending || isRunning) return;
    const { request, resume } = pending;
    if (handledRef.current === pending) return;
    handledRef.current = pending;
    void executeWordTool(request, envRef.current)
      .then((result) => {
        const tally = insertTally(request.tool, result);
        if (tally) trackInsert(request.tool, tally);
        if (request.tool === "edit_document") {
          setNotice(noticeFor(result as EditResult));
          onAfterEditRef.current?.();
        } else if (request.tool === "insert_document_image") {
          onAfterEditRef.current?.();
        } else if (request.tool === "add_document_comment") {
          setNotice(commentNoticeFor(result as CommentResult));
        } else if (request.tool === "format_document") {
          setNotice(formatNoticeFor(result as FormatResult));
          onAfterEditRef.current?.();
        } else if (
          request.tool === "review_document_changes" ||
          request.tool === "apply_template_styles" ||
          request.tool === "apply_template" ||
          request.tool === "set_page_setup" ||
          request.tool === "pull_from_mytender" ||
          request.tool === "write_question_answer" ||
          request.tool === "insert_template_pages" ||
          request.tool === "apply_template_headers_footers" ||
          request.tool === "undo_last_change"
        ) {
          onAfterEditRef.current?.();
        }
        resume({ type: WORD_TOOL_INTERRUPT_TYPE, call_id: request.callId, result });
      })
      .catch((error: unknown) => {
        if (isDocumentInsert(request.tool)) trackInsert(request.tool, { succeeded: 0, failed: 1 });
        console.warn("Word tool request failed; resuming with an error", error);
        resume({
          type: WORD_TOOL_INTERRUPT_TYPE,
          call_id: request.callId,
          error:
            error instanceof Error ? error.message : "The add-in could not carry out this request in the document.",
        });
      });
  }, [pending, isRunning]);

  const dismiss = useCallback(() => setNotice(null), []);
  return { notice, dismiss };
};
